'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ThspianIconLogo from '../../components/ThspianLogo/ThspianIconLogo';
import { useNavigate } from 'react-router-dom';
import { api } from '../../configurations/api';
import { getCurrentUserWebNotificationsUrl, webNotificationsUrl } from '../../configurations/api/url';
import { httpRequest } from '../../redux/types';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { cutoffAndAddPlus } from '../../utils/AppUtils';
import {
    BRAND_LUXE_ERROR,
    BRAND_LUXE_GOLD,
    BRAND_LUXE_MUTED,
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';

const PAGE_SIZE = 20;

type BrandNotifKind = 'application' | 'message' | 'live_url' | 'content' | 'review' | 'pause' | 'system' | string;

export interface IBrandWebNotification {
    _id: string;
    seen?: boolean;
    payload?: { title?: string; body?: string };
    createdAt?: string;
    created_at?: string;
    additional_data?: {
        audience?: string;
        kind?: BrandNotifKind;
        campaign_id?: string | null;
        participant_id?: string | null;
        link?: string | null;
    };
}

function kindIcon() {
    return <ThspianIconLogo height={22} width={22} fill={BRAND_LUXE_GOLD} />;
}

function formatWhen(iso?: string) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    } catch {
        return '';
    }
}

function resolveLink(n: IBrandWebNotification): string | null {
    const data = n.additional_data || {};
    if (data.link) return data.link;
    if (data.campaign_id && data.participant_id) {
        return `/brand/campaigns/${data.campaign_id}/participant/${data.participant_id}`;
    }
    if (data.campaign_id) return `/brand/campaigns/${data.campaign_id}`;
    return null;
}

function isNotificationSeen(n: IBrandWebNotification, locallySeen: Set<string>) {
    if (n._id && locallySeen.has(n._id)) return true;
    return n.seen === true;
}

function listDeduped(prev: IBrandWebNotification[], next: IBrandWebNotification[]) {
    const seen = new Set(prev.map(n => n._id));
    return [...prev, ...next.filter(n => n._id && !seen.has(n._id))];
}

const LOCALLY_SEEN_STORAGE_KEY = 'thspian.brandNotifications.seenIds';

function loadLocallySeenIds(): Set<string> {
    try {
        const raw = sessionStorage.getItem(LOCALLY_SEEN_STORAGE_KEY);
        if (!raw) return new Set();
        const parsed = JSON.parse(raw);
        return new Set(Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : []);
    } catch {
        return new Set();
    }
}

function persistLocallySeenIds(ids: Set<string>) {
    try {
        sessionStorage.setItem(LOCALLY_SEEN_STORAGE_KEY, JSON.stringify([...ids]));
    } catch {
        // ignore quota / private mode
    }
}

export function BrandNotificationsBell() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState<IBrandWebNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const listRef = useRef<HTMLDivElement | null>(null);
    const fetchingRef = useRef(false);
    /**
     * Clicked-to-read ids for this session.
     * - Each click marks one read and drops the badge count by one
     * - Bell red badge stays until every notification is read (count === 0)
     */
    const locallySeenIdsRef = useRef<Set<string>>(loadLocallySeenIds());

    const markLocallySeen = useCallback((id: string) => {
        locallySeenIdsRef.current.add(id);
        persistLocallySeenIds(locallySeenIdsRef.current);
    }, []);

    const applySeenOverlay = useCallback((rows: IBrandWebNotification[]) => {
        const local = locallySeenIdsRef.current;
        return rows.map(n => (isNotificationSeen(n, local) ? { ...n, seen: true } : n));
    }, []);

    const syncUnreadFromRows = useCallback((rows: IBrandWebNotification[]) => {
        // Badge count = remaining unread only; never clear the red dot early.
        setUnreadCount(rows.filter(n => !isNotificationSeen(n, locallySeenIdsRef.current)).length);
    }, []);

    const fetchPage = useCallback(
        async (pageNum: number, append: boolean) => {
            if (fetchingRef.current) return;
            fetchingRef.current = true;
            if (append) setLoadingMore(true);
            else setLoading(true);
            try {
                const qs = new URLSearchParams({
                    audience: 'brand',
                    page: String(pageNum),
                    limit: String(PAGE_SIZE),
                    sort: '-createdAt',
                });
                // api() already returns response.data (the JSON body)
                const payload = await api(
                    `${getCurrentUserWebNotificationsUrl}?${qs.toString()}`,
                    httpRequest.GET,
                    null,
                );
                const nested = payload?.data;
                const rows: IBrandWebNotification[] = Array.isArray(nested?.data)
                    ? nested.data
                    : Array.isArray(nested)
                    ? nested
                    : Array.isArray(payload)
                    ? payload
                    : [];
                const merged = applySeenOverlay(rows);
                setItems(prev => {
                    const next = append ? applySeenOverlay(listDeduped(prev, merged)) : merged;
                    syncUnreadFromRows(next);
                    return next;
                });
                setPage(Number(payload?.currentPage) || pageNum);
                setTotalPages(Math.max(1, Number(payload?.totalPages) || 1));
            } catch (err) {
                console.error('[BrandNotificationsBell] fetch failed', err);
                if (!append) setItems([]);
            } finally {
                setLoading(false);
                setLoadingMore(false);
                fetchingRef.current = false;
            }
        },
        [applySeenOverlay, syncUnreadFromRows],
    );

    useEffect(() => {
        void fetchPage(1, false);
    }, [fetchPage]);

    useEffect(() => {
        if (open) void fetchPage(1, false);
    }, [open, fetchPage]);

    // Poll so application / message / wallet rows appear without reopening the drawer.
    useEffect(() => {
        const id = window.setInterval(() => {
            if (!document.hidden) void fetchPage(1, false);
        }, 45000);
        return () => window.clearInterval(id);
    }, [fetchPage]);

    const onScroll = () => {
        const el = listRef.current;
        if (!el || loadingMore || loading || page >= totalPages) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
            void fetchPage(page + 1, true);
        }
    };

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleClickItem = async (n: IBrandWebNotification) => {
        const link = resolveLink(n);
        const alreadySeen = isNotificationSeen(n, locallySeenIdsRef.current);

        // Mark this one read and reduce the badge by one; leave the red badge
        // up until every remaining notification is also read.
        if (!alreadySeen && n._id) {
            markLocallySeen(n._id);
            setItems(prev => {
                const next = prev.map(x => (x._id === n._id ? { ...x, seen: true } : x));
                syncUnreadFromRows(next);
                return next;
            });
            // Persist in background — don't block navigation on the PATCH.
            void api(`${webNotificationsUrl}/${n._id}`, httpRequest.PATCH, { seen: true }, 1, 2000).catch(
                (err: unknown) => console.error('[BrandNotificationsBell] mark seen failed', err),
            );
        }

        handleClose();
        if (link) navigate(link);
    };

    return (
        <>
            <IconButton
                aria-label="Notifications"
                onClick={handleOpen}
                sx={{
                    color: BRAND_LUXE_MUTED,
                    position: 'relative',
                    width: 40,
                    height: 40,
                    '&:hover': { color: BRAND_LUXE_GOLD, bgcolor: 'rgba(255,193,7,0.08)' },
                }}>
                <NotificationsNoneOutlinedIcon />
                {unreadCount > 0 ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            minWidth: 16,
                            height: 16,
                            px: 0.4,
                            borderRadius: 999,
                            bgcolor: BRAND_LUXE_ERROR,
                            color: '#0e0e0e',
                            fontSize: 9,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                        }}>
                        {cutoffAndAddPlus(unreadCount, 99)}
                    </Box>
                ) : null}
            </IconButton>

            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 'min(420px, 100vw)' },
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        borderLeft: '1px solid rgba(255,255,255,0.06)',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}>
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 16,
                                color: BRAND_LUXE_PRIMARY,
                            }}>
                            Notifications
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                                color: BRAND_LUXE_MUTED,
                                mt: 0.25,
                            }}>
                            {unreadCount > 0 ? `${unreadCount} unread` : 'You are all caught up'}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: BRAND_LUXE_MUTED }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box ref={listRef} onScroll={onScroll} sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5 }}>
                    {loading && items.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={28} sx={{ color: BRAND_LUXE_GOLD }} />
                        </Box>
                    ) : null}

                    {!loading && items.length === 0 ? (
                        <Typography
                            sx={{
                                textAlign: 'center',
                                color: BRAND_LUXE_MUTED,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 13,
                                py: 6,
                            }}>
                            No notifications yet
                        </Typography>
                    ) : null}

                    {items.map(n => {
                        const unread = !isNotificationSeen(n, locallySeenIdsRef.current);
                        return (
                            <Box
                                key={n._id}
                                onClick={() => void handleClickItem(n)}
                                sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    p: 1.75,
                                    mb: 1,
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    bgcolor: unread ? 'rgba(255,201,101,0.08)' : BRAND_SURFACE_CONTAINER,
                                    border: unread
                                        ? '1px solid rgba(255,201,101,0.22)'
                                        : '1px solid rgba(255,255,255,0.04)',
                                    '&:hover': {
                                        borderColor: 'rgba(255,201,101,0.35)',
                                    },
                                }}>
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '50%',
                                        bgcolor: 'rgba(255,201,101,0.12)',
                                        color: BRAND_LUXE_GOLD,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                    {kindIcon()}
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 13,
                                            color: '#fff',
                                            lineHeight: 1.3,
                                        }}>
                                        {n.payload?.title || 'Notification'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 12,
                                            color: BRAND_LUXE_MUTED,
                                            mt: 0.35,
                                            lineHeight: 1.4,
                                        }}>
                                        {n.payload?.body || ''}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 10,
                                            color: 'rgba(255,255,255,0.35)',
                                            mt: 0.75,
                                        }}>
                                        {formatWhen(n.createdAt || n.created_at)}
                                    </Typography>
                                </Box>
                                {unread ? (
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: BRAND_LUXE_GOLD,
                                            mt: 0.75,
                                            flexShrink: 0,
                                        }}
                                    />
                                ) : null}
                            </Box>
                        );
                    })}

                    {loadingMore ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={22} sx={{ color: BRAND_LUXE_GOLD }} />
                        </Box>
                    ) : null}
                </Box>
            </Drawer>
        </>
    );
}
